import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Sparkles,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import * as ownerApi from "../../api/payroll/leaveTypeApi";
import * as portalApi from "../../api/payroll/leaveTypePortalApi";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const LEAVE_TYPE_TEMPLATES = [
  { name: "Sick Leave", code: "SICK", isPaid: true, maxDaysPerYear: 12, description: "Medical / illness leave" },
  { name: "Casual Leave", code: "CASUAL", isPaid: true, maxDaysPerYear: 12, description: "Short personal leave" },
  { name: "Earned Leave", code: "EARNED", isPaid: true, maxDaysPerYear: 15, description: "Accumulated privilege leave" },
  { name: "Maternity Leave", code: "MATERNITY", isPaid: true, maxDaysPerYear: 180, description: "Maternity leave" },
  { name: "Paternity Leave", code: "PATERNITY", isPaid: true, maxDaysPerYear: 15, description: "Paternity leave" },
  { name: "Unpaid Leave", code: "UNPAID", isPaid: false, maxDaysPerYear: null, description: "Leave without pay (counts as LOP in payroll)" },
  { name: "Other", code: "OTHER", isPaid: false, maxDaysPerYear: null, description: "Other leave" },
];

const emptyForm = {
  name: "",
  code: "",
  isPaid: true,
  maxDaysPerYear: "",
  description: "",
};

export default function LeaveTypeForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/leave-types" : "/leave-types";
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [templateKey, setTemplateKey] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAllLeaveTypes();
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            name: found.name || "",
            code: found.code || "",
            isPaid: found.isPaid !== false,
            maxDaysPerYear: found.maxDaysPerYear ?? "",
            description: found.description || "",
          });
        } else {
          toast.error("Leave type not found");
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, isPortal]);

  const applyTemplate = (code) => {
    setTemplateKey(code);
    if (!code) {
      setForm(emptyForm);
      return;
    }
    const t = LEAVE_TYPE_TEMPLATES.find((x) => x.code === code);
    if (!t) return;
    setForm({
      name: t.name,
      code: t.code,
      isPaid: t.isPaid,
      maxDaysPerYear: t.maxDaysPerYear ?? "",
      description: t.description || "",
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        isPaid: form.isPaid,
        maxDaysPerYear:
          form.maxDaysPerYear === "" || form.maxDaysPerYear === null
            ? null
            : Number(form.maxDaysPerYear),
        description: form.description.trim(),
      };
      if (!payload.name || !payload.code) {
        toast.error("Name and code are required");
        setLoading(false);
        return;
      }
      if (isEdit) {
        await api.updateLeaveType(id, payload);
        toast.success("Leave type updated");
      } else {
        await api.createLeaveType(payload);
        toast.success("Leave type created");
      }
      setTimeout(() => navigate(basePath), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const templateOptions = [
    { label: "Custom / Blank", value: "" },
    ...LEAVE_TYPE_TEMPLATES.map((t) => ({
      label: `${t.name} (${t.code})${t.isPaid ? "" : " — unpaid"}`,
      value: t.code,
    })),
  ];

  return (
    <div className="p-4 sm:p-6 max-w-xl mx-auto space-y-6">
      {/* Header */}
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-800">
        <button
          type="button"
          onClick={() => navigate(basePath)}
          className="inline-flex items-center text-body-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-primary-600 transition-colors mb-1"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to List
        </button>
        <h1 className="text-title-lg md:text-headline-lg font-display font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          {isEdit ? "Edit Leave Type" : "Create Leave Type"}
        </h1>
      </div>

      

      

      <Card>
        <CardHeader className="bg-neutral-50/50 border-b border-neutral-200 dark:border-neutral-800">
          <CardTitle className="text-title-md font-display font-semibold text-neutral-900 dark:text-neutral-100">
            Leave Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isEdit && (
              <div className="bg-neutral-50 dark:bg-neutral-950 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg space-y-2">
                <label className="block text-body-sm font-semibold text-neutral-700 dark:text-neutral-200 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Prefill from Template
                </label>
                <Select
                  options={templateOptions}
                  value={templateKey}
                  onChange={(val) => applyTemplate(val)}
                />
                <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
                  Pick a common preset to auto-populate default settings.
                </p>
              </div>
            )}

            <div>
              <Input
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="e.g. Sick Leave"
              />
            </div>

            <div>
              <Input
                label="Code"
                name="code"
                value={form.code}
                onChange={handleChange}
                required
                className="uppercase font-mono"
                placeholder="e.g. SICK"
              />
            </div>

            <div className="pt-1">
              <Checkbox
                id="isPaid"
                name="isPaid"
                checked={form.isPaid}
                onChange={handleChange}
                label="Paid leave"
                helperText="Unpaid leave counts as LOP in payroll"
              />
            </div>

            <div>
              <Input
                label="Max Days / Year (optional)"
                name="maxDaysPerYear"
                type="number"
                min="0"
                value={form.maxDaysPerYear}
                onChange={handleChange}
                placeholder="Unlimited if left empty"
              />
            </div>

            <div>
              <Textarea
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                placeholder="Optional description or criteria for this leave type"
              />
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <Button
                type="submit"
                isLoading={loading}
                variant="primary"
                leftIcon={<Save className="h-4 w-4" />}
              >
                {isEdit ? "Update Leave Type" : "Create Leave Type"}
              </Button>
              <Button
                type="button"
                onClick={() => navigate(basePath)}
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}